<script setup lang="ts">
import { ref, watch, onUnmounted, onMounted } from 'vue'
import type { GameOptions } from '@/types'
import type { MediaDeviceInfo } from '@/types'
import type { ConnectionType } from '@/types'

const props = defineProps<{
  show: boolean
  cameras: MediaDeviceInfo[]
  selectedCamera: string
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  'camera-change': [deviceId: string]
}>()


const previewVideo = ref<HTMLVideoElement | null>(null)
let currentStream: MediaStream | null = null

// Tipo de conexión
const connectionType = ref<ConnectionType>('internet')

onMounted(() => {
	const saved = localStorage.getItem('connectionType') as ConnectionType
	if (saved === 'internet' || saved === 'lan') {
		connectionType.value = saved
	}
})

const selectConnection = (type: ConnectionType) => {
	connectionType.value = type
	localStorage.setItem('connectionType', type)
}

const close = () => {
  emit('update:show', false)
  stopPreview()
}

const handleCameraChange = async (event: Event) => {
  const target = event.target as HTMLSelectElement
  const deviceId = target.value
  if (deviceId) {
    emit('camera-change', deviceId)
    await startPreview(deviceId)
  } else {
    stopPreview()
  }
}

const startPreview = async (deviceId: string) => {
  stopPreview()

  try {
    const constraints = {
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 320 },
        height: { ideal: 240 }
      }
    }

    console.log('[OptionsModal] 🔍 Iniciando preview con deviceId:', deviceId.slice(0, 8) + '...')
    console.log('[OptionsModal] 🔍 Constraints COMPLETAS:', JSON.stringify(constraints, null, 2))

    const stream = await navigator.mediaDevices.getUserMedia(constraints)

    console.log('[OptionsModal] ✅ Stream obtenido con éxito')
    console.log('[OptionsModal] 📷 Track settings:', stream.getVideoTracks()[0]?.getSettings())

    currentStream = stream
    if (previewVideo.value) {
      previewVideo.value.srcObject = stream
    }
  } catch (err) {
    console.error('[CameraPreview] Error starting preview:', err)
  }
}

const stopPreview = () => {
  if (currentStream) {
    currentStream.getTracks().forEach(track => track.stop())
    currentStream = null
  }
  if (previewVideo.value) {
    previewVideo.value.srcObject = null
  }
}

// Cleanup cuando se cierra el modal
onUnmounted(() => {
  stopPreview()
})

// Watcher para iniciar preview cuando se abre el modal y hay cámara seleccionada
watch(() => props.selectedCamera, (newDeviceId) => {
  if (newDeviceId && props.show) {
    startPreview(newDeviceId)
  } else {
    stopPreview()
  }
}, { immediate: true })

// Watcher adicional para cuando se abre/cierra el modal
watch(() => props.show, (isOpen) => {
  if (isOpen && props.selectedCamera) {
    startPreview(props.selectedCamera)
  } else if (!isOpen) {
    stopPreview()
  }
})
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
    <div class="glass rounded-3xl p-6 max-w-md w-full animate-fade-in">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-white">⚙️ Opciones del Juego</h2>
        <button @click="close" class="text-white text-2xl hover:scale-110 transition-transform">✕</button>
      </div>

      <!-- Placeholder para futuras opciones generales del juego -->
      <!-- Ejemplos: Sonidos, idioma, tema, accesibilidad, etc. -->
      <!-- <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">🔊 Sonidos</label>
        <div class="grid grid-cols-2 gap-2">
          <button class="py-3 px-4 rounded-xl font-bold transition-colors bg-accent text-dark">
            Activados
          </button>
          <button class="py-3 px-4 rounded-xl font-bold transition-colors bg-white/20 text-white">
            Silencio
          </button>
        </div>
      </div> -->

      <div v-if="cameras.length > 0" class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">📷 Cámara para escanear QR</label>

        <!-- Preview de cámara -->
        <div v-if="selectedCamera" class="mb-3">
          <video
            ref="previewVideo"
            autoplay
            playsinline
            muted
            class="w-full h-32 object-cover rounded-lg bg-black"
          />
        </div>

        <select
          :value="selectedCamera"
          @change="handleCameraChange"
          class="w-full bg-white/10 text-white rounded-lg px-3 py-2 border border-white/20 focus:border-accent focus:outline-none"
        >
          <option value="">Automática (recomendado)</option>
          <option
            v-for="camera in cameras"
            :key="camera.deviceId"
            :value="camera.deviceId"
          >
            {{ camera.label || `Cámara ${camera.deviceId.slice(0, 8)}...` }}
          </option>
        </select>
        <p class="text-white/60 text-xs mt-2">Selecciona la cámara que prefieres usar para escanear códigos QR</p>
      </div>

      <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">🌐 Tipo de Conexión</label>
        <div class="grid grid-cols-2 gap-3">
          <button
            @click="selectConnection('internet')"
            :class="connectionType === 'internet' ? 'bg-accent text-dark ring-2 ring-accent/50' : 'bg-white/20 text-white hover:bg-white/30'"
            class="py-4 px-4 rounded-xl font-bold transition-all hover:scale-105 flex flex-col items-center gap-2"
          >
            <span class="text-2xl">🌐</span>
            <span>Internet</span>
          </button>
          <button
            @click="selectConnection('lan')"
            :class="connectionType === 'lan' ? 'bg-accent text-dark ring-2 ring-accent/50' : 'bg-white/20 text-white hover:bg-white/30'"
            class="py-4 px-4 rounded-xl font-bold transition-all hover:scale-105 flex flex-col items-center gap-2"
          >
            <span class="text-2xl">📶</span>
            <span>Red Local</span>
          </button>
        </div>
        <p class="text-white/60 text-xs mt-2">
          {{ connectionType === 'lan'
            ? '📶 Conexión sin internet en misma red WiFi (más rápido)'
            : '🌐 Conexión online desde cualquier lugar con internet' }}
        </p>
      </div>

      <button
        @click="close"
        class="w-full py-4 px-6 bg-accent text-dark font-bold rounded-xl text-lg hover:scale-105 transition-transform"
      >
        ✓ Guardar
      </button>
    </div>
  </div>
</template>
